import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  CaretRight,
  MagnifyingGlass,
  Plus,
} from "@phosphor-icons/react";
import {
  Link,
  useSearchParams,
} from "react-router-dom";
import { toast } from "sonner";

import {
  gmao,
  users as usersApi,
} from "@/api";
import SavTicketFormModal from "@/components/gmao/SavTicketFormModal";
import { useAuth } from "@/context/AuthContext";
import { hasPageAccess } from "@/lib/accessPermissions";
import { formatApiError } from "@/lib/api";


const STATUS_LABELS = {
  open: "Ouvert",
  taken: "Pris en charge",
  assigned: "Assigné",
  in_progress: "En cours",
  resolved: "Résolu",
  closed: "Fermé",
  cancelled: "Annulé",
};


const STATUS_OPTIONS = [
  "",
  "open",
  "taken",
  "assigned",
  "in_progress",
  "resolved",
  "closed",
  "cancelled",
];


const URGENCY_LABELS = {
  low: "Faible",
  normal: "Normale",
  high: "Haute",
  urgent: "Urgente",
};


const URGENCY_CLASSES = {
  low:
    "border-[var(--do-border)] bg-[var(--do-bg-2)] text-[var(--do-muted)]",
  normal:
    "border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]",
  high:
    "border-[var(--do-warning-border)] bg-[var(--do-warning-soft)] text-[var(--do-warning)]",
  urgent:
    "border-[var(--do-danger-border)] bg-[var(--do-danger-soft)] text-[var(--do-danger)]",
};


const STATUS_CLASSES = {
  open:
    "border-[var(--do-border)] bg-[var(--do-bg-2)] text-[var(--do-ink)]",
  taken:
    "border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]",
  assigned:
    "border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]",
  in_progress:
    "border-[var(--do-warning-border)] bg-[var(--do-warning-soft)] text-[var(--do-warning)]",
  resolved:
    "border-[var(--do-success-border)] bg-[var(--do-success-soft)] text-[var(--do-success)]",
  closed:
    "border-[var(--do-border)] bg-[var(--do-bg-2)] text-[var(--do-muted)]",
  cancelled:
    "border-[var(--do-danger-border)] bg-[var(--do-danger-soft)] text-[var(--do-danger)]",
};


function formatDate(value) {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString(
    "fr-FR",
    {
      dateStyle: "short",
      timeStyle: "short",
    }
  );
}


function minutesLabel(value) {
  if (
    value === null ||
    value === undefined
  ) {
    return "—";
  }

  if (value < 60) {
    return `${value} min`;
  }

  const hours =
    Math.floor(value / 60);

  const minutes =
    value % 60;

  if (!minutes) {
    return `${hours} h`;
  }

  return `${hours} h ${minutes}`;
}


function ticketTarget(
  ticket,
  canOpenIntervention
) {
  if (
    canOpenIntervention &&
    ticket.interventionId
  ) {
    return (
      `/app/gmao/interventions/${encodeURIComponent(
        ticket.interventionId
      )}`
    );
  }

  return (
    `/app/gmao/sav/${encodeURIComponent(
      ticket.id
    )}`
  );
}


export default function GmaoSavTickets() {
  const { user } = useAuth();

  const [
    searchParams,
    setSearchParams,
  ] = useSearchParams();

  const canManage = [
    "admin",
    "maintenance_manager",
  ].includes(user?.role);

  const canCreate = [
    "admin",
    "manager",
    "maintenance_manager",
    "technician",
  ].includes(user?.role);

  const canOpenIntervention =
    hasPageAccess(
      user,
      "gmao",
      "interventions"
    );

  const requestedStatus =
    searchParams.get("status") || "";

  const requestedAssignment =
    searchParams.get("assignment") || "";

  const requestedView =
    searchParams.get("view") || "all";

  const openScopeRequested =
    searchParams.get("scope") === "open";

  const [tickets, setTickets] =
    useState([]);

  const [machines, setMachines] =
    useState([]);

  const [team, setTeam] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [saving, setSaving] =
    useState(false);

  const [createOpen, setCreateOpen] =
    useState(false);

  const [search, setSearch] =
    useState("");

  const [status, setStatus] =
    useState(
      STATUS_OPTIONS.includes(
        requestedStatus
      )
        ? requestedStatus
        : ""
    );

  const [urgency, setUrgency] =
    useState("");

  const [
    assignment,
    setAssignment,
  ] = useState(
    requestedAssignment
  );

  const [view, setView] =
    useState(
      [
        "all",
        "open",
        "in_progress",
        "resolved",
      ].includes(requestedView)
        ? requestedView
        : "all"
    );


  useEffect(() => {
    setStatus(
      STATUS_OPTIONS.includes(
        requestedStatus
      )
        ? requestedStatus
        : ""
    );
  }, [requestedStatus]);


  useEffect(() => {
    setAssignment(
      requestedAssignment
    );
  }, [requestedAssignment]);


  useEffect(() => {
    setView(
      [
        "all",
        "open",
        "in_progress",
        "resolved",
      ].includes(requestedView)
        ? requestedView
        : "all"
    );
  }, [requestedView]);


  const load = useCallback(
    async () => {
      setLoading(true);

      try {
        const [
          ticketData,
          machineData,
          teamData,
        ] = await Promise.all([
          gmao.listSavTickets({
            limit: 500,
          }),
          canCreate
            ? gmao.listMachines({
                limit: 500,
              })
            : Promise.resolve([]),
          canManage
            ? usersApi.list()
            : Promise.resolve([]),
        ]);

        setTickets(
          Array.isArray(
            ticketData?.items
          )
            ? ticketData.items
            : []
        );

        setMachines(
          Array.isArray(machineData)
            ? machineData
            : []
        );

        const users =
          Array.isArray(teamData)
            ? teamData
            : Array.isArray(
                teamData?.items
              )
              ? teamData.items
              : [];

        setTeam(users);
      } catch (error) {
        toast.error(
          formatApiError(
            error.response?.data?.detail
          ) ||
            "Impossible de charger les tickets SAV."
        );
      } finally {
        setLoading(false);
      }
    },
    [
      canCreate,
      canManage,
    ]
  );


  useEffect(() => {
    load();
  }, [load]);


  const technicians = useMemo(
    () =>
      team.filter(
        (member) =>
          member.role ===
            "technician" &&
          member.deleted !== true &&
          member.isActive !== false
      ),
    [team]
  );


  const filteredBase = useMemo(
    () => {
      const cleanSearch =
        search
          .trim()
          .toLocaleLowerCase("fr");

      return tickets.filter(
        (ticket) => {
          if (
            openScopeRequested &&
            ![
              "open",
              "taken",
              "assigned",
              "in_progress",
            ].includes(ticket.status)
          ) {
            return false;
          }

          if (
            urgency &&
            ticket.urgency !== urgency
          ) {
            return false;
          }

          if (
            assignment ===
              "unassigned" &&
            ticket.assignedTechnicianId
          ) {
            return false;
          }

          if (
            assignment &&
            assignment !==
              "unassigned" &&
            ticket.assignedTechnicianId !==
              assignment
          ) {
            return false;
          }

          if (!cleanSearch) {
            return true;
          }

          const haystack = [
            ticket.ticketNumber,
            ticket.clientName,
            ticket.customerName,
            ticket.company,
            ticket.machineName,
            ticket.machineSerialNumber,
            ticket.machineSiteName,
            ticket.technicianName,
            ticket.description,
            ticket.issueLabel,
          ]
            .filter(Boolean)
            .join(" ")
            .toLocaleLowerCase(
              "fr"
            );

          return haystack.includes(
            cleanSearch
          );
        }
      );
    },
    [
      assignment,
      openScopeRequested,
      search,
      tickets,
      urgency,
    ]
  );


  const counts = useMemo(
    () => ({
      total:
        filteredBase.length,

      open:
        filteredBase.filter(
          (ticket) =>
            ticket.status === "open"
        ).length,

      inProgress:
        filteredBase.filter(
          (ticket) =>
            [
              "taken",
              "assigned",
              "in_progress",
            ].includes(ticket.status)
        ).length,

      resolved:
        filteredBase.filter(
          (ticket) =>
            ticket.status ===
            "resolved"
        ).length,
    }),
    [filteredBase]
  );


  const visibleTickets = useMemo(
    () =>
      filteredBase.filter(
        (ticket) => {
          if (
            status &&
            ticket.status !== status
          ) {
            return false;
          }

          if (
            view === "open"
          ) {
            return (
              ticket.status ===
              "open"
            );
          }

          if (
            view ===
            "in_progress"
          ) {
            return [
              "taken",
              "assigned",
              "in_progress",
            ].includes(
              ticket.status
            );
          }

          if (
            view === "resolved"
          ) {
            return (
              ticket.status ===
              "resolved"
            );
          }

          return true;
        }
      ),
    [
      filteredBase,
      status,
      view,
    ]
  );


  const updateUrlParam = (
    name,
    value
  ) => {
    const nextParams =
      new URLSearchParams(
        searchParams
      );

    if (
      !value ||
      value === "all"
    ) {
      nextParams.delete(name);
    } else {
      nextParams.set(
        name,
        value
      );
    }

    setSearchParams(
      nextParams,
      {
        replace: true,
      }
    );
  };


  const changeStatus = (
    value
  ) => {
    setStatus(value);
    setView("all");

    const nextParams =
      new URLSearchParams(
        searchParams
      );

    if (value) {
      nextParams.set(
        "status",
        value
      );
    } else {
      nextParams.delete(
        "status"
      );
    }

    nextParams.delete(
      "view"
    );

    setSearchParams(
      nextParams,
      {
        replace: true,
      }
    );
  };


  const changeView = (
    value
  ) => {
    setView(value);
    setStatus("");

    const nextParams =
      new URLSearchParams(
        searchParams
      );

    nextParams.delete(
      "status"
    );

    if (value === "all") {
      nextParams.delete(
        "view"
      );
    } else {
      nextParams.set(
        "view",
        value
      );
    }

    setSearchParams(
      nextParams,
      {
        replace: true,
      }
    );
  };


  const createTicket =
    async (payload) => {
      setSaving(true);

      try {
        await gmao.createSavTicket(
          payload
        );

        toast.success(
          "Ticket SAV créé"
        );

        setCreateOpen(false);

        await load();
      } catch (error) {
        toast.error(
          formatApiError(
            error.response?.data?.detail
          ) ||
            "Impossible de créer le ticket SAV."
        );
      } finally {
        setSaving(false);
      }
    };


  const workflowCards = [
    {
      key: "all",
      label: "Total",
      value: counts.total,
    },
    {
      key: "open",
      label: "Ouverts",
      value: counts.open,
    },
    {
      key: "in_progress",
      label: "En cours",
      value:
        counts.inProgress,
    },
    {
      key: "resolved",
      label: "Résolus",
      value:
        counts.resolved,
    },
  ];


  return (
    <main
      className="acta-gmao-page-standard w-full max-w-full min-w-0 p-4 sm:p-5 lg:p-8"
      data-testid="sav-ticket-list"
    >
      <div className="overflow-x-auto pb-1">
        <div className="flex min-w-max items-center gap-2 rounded-xl border border-[var(--do-border)] bg-[var(--do-surface)] p-2">
          <div className="relative w-[250px] lg:w-[320px]">
            <MagnifyingGlass
              size={17}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--do-muted)]"
            />

            <input
              type="search"
              value={search}
              onChange={(event) =>
                setSearch(
                  event.target.value
                )
              }
              placeholder="Rechercher client, machine, n° série…"
              className="h-10 w-full rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] pl-9 pr-3 text-sm outline-none transition focus:border-[var(--do-accent)]"
              data-testid="sav-ticket-search"
            />
          </div>

          <select
            value={status}
            onChange={(event) =>
              changeStatus(
                event.target.value
              )
            }
            className="h-10 min-w-[145px] rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm"
            data-testid="sav-ticket-status-filter"
          >
            {STATUS_OPTIONS.map(
              (item) => (
                <option
                  key={
                    item || "all"
                  }
                  value={item}
                >
                  {item
                    ? STATUS_LABELS[
                        item
                      ]
                    : "Statut"}
                </option>
              )
            )}
          </select>

          <select
            value={urgency}
            onChange={(event) =>
              setUrgency(
                event.target.value
              )
            }
            className="h-10 min-w-[130px] rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm"
            data-testid="sav-ticket-urgency-filter"
          >
            <option value="">
              Urgence
            </option>
            {Object.entries(
              URGENCY_LABELS
            ).map(
              ([
                value,
                label,
              ]) => (
                <option
                  key={value}
                  value={value}
                >
                  {label}
                </option>
              )
            )}
          </select>

          {canManage && (
            <select
              value={assignment}
              onChange={(event) => {
                const value =
                  event.target.value;

                setAssignment(
                  value
                );

                updateUrlParam(
                  "assignment",
                  value
                );
              }}
              className="h-10 min-w-[160px] rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm"
              data-testid="sav-ticket-assignment-filter"
            >
              <option value="">
                Technicien
              </option>

              <option value="unassigned">
                Non affecté
              </option>

              {technicians.map(
                (technician) => {
                  const name = [
                    technician.firstName,
                    technician.lastName,
                  ]
                    .filter(
                      Boolean
                    )
                    .join(" ");

                  return (
                    <option
                      key={
                        technician.id
                      }
                      value={
                        technician.id
                      }
                    >
                      {name ||
                        technician.email ||
                        "Technicien"}
                    </option>
                  );
                }
              )}
            </select>
          )}

          {canCreate && (
            <button
              type="button"
              onClick={() =>
                setCreateOpen(
                  true
                )
              }
              className="inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-lg bg-[var(--do-accent)] px-4 text-sm font-heading font-bold text-white transition hover:opacity-90"
              data-testid="sav-ticket-create"
            >
              <Plus
                size={17}
                weight="bold"
              />
              Nouveau ticket
            </button>
          )}
        </div>
      </div>

      <section
        className="mt-3 grid grid-cols-4 gap-2"
        data-testid="sav-ticket-kpis"
      >
        {workflowCards.map(
          (card) => {
            const active =
              view ===
                card.key &&
              !status;

            return (
              <button
                key={card.key}
                type="button"
                onClick={() =>
                  changeView(
                    card.key
                  )
                }
                className={[
                  "min-w-0 rounded-xl border px-2 py-2.5 text-left transition sm:px-3",
                  active
                    ? "border-[var(--do-accent)] bg-[var(--do-accent-soft)]"
                    : "border-[var(--do-border)] bg-[var(--do-surface)] hover:bg-[var(--do-surface-soft)]",
                ].join(" ")}
                data-testid={`sav-ticket-kpi-${card.key}`}
              >
                <div className="truncate text-[10px] font-bold uppercase tracking-wide text-[var(--do-muted)] sm:text-xs">
                  {card.label}
                </div>

                <div className="mt-0.5 font-heading text-lg font-black sm:text-xl">
                  {card.value}
                </div>
              </button>
            );
          }
        )}
      </section>

      <section className="mt-3">
        {loading ? (
          <div className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface)] p-5 text-sm text-[var(--do-muted)]">
            Chargement des tickets SAV…
          </div>
        ) : visibleTickets.length ===
          0 ? (
          <div className="rounded-xl border border-[var(--do-border)] bg-[var(--do-surface)] p-5 text-sm text-[var(--do-muted)]">
            Aucun ticket SAV pour ces filtres.
          </div>
        ) : (
          <>
            <div className="hidden overflow-hidden rounded-xl border border-[var(--do-border)] bg-[var(--do-surface)] md:block">
              <div className="grid grid-cols-[1.35fr_.8fr_1.2fr_1.1fr_1fr_.8fr_.65fr] gap-3 border-b border-[var(--do-border)] bg-[var(--do-bg-2)] px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-[var(--do-muted)]">
                <div>Ticket</div>
                <div>Urgence</div>
                <div>Client</div>
                <div>Machine</div>
                <div>Technicien</div>
                <div>Statut</div>
                <div>Délai</div>
              </div>

              <div className="divide-y divide-[var(--do-border)]">
                {visibleTickets.map(
                  (ticket) => (
                    <Link
                      key={ticket.id}
                      to={ticketTarget(
                        ticket,
                        canOpenIntervention
                      )}
                      className="grid grid-cols-[1.35fr_.8fr_1.2fr_1.1fr_1fr_.8fr_.65fr] items-center gap-3 px-4 py-3 transition hover:bg-[var(--do-surface-soft)]"
                      data-testid="sav-ticket-row"
                    >
                      <div className="min-w-0">
                        <div className="truncate font-heading text-sm font-black">
                          {ticket.ticketNumber}
                        </div>

                        <div className="mt-0.5 line-clamp-1 text-xs text-[var(--do-muted)]">
                          {ticket.description}
                        </div>
                      </div>

                      <div>
                        <span
                          className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-bold ${
                            URGENCY_CLASSES[
                              ticket.urgency
                            ] ||
                            URGENCY_CLASSES.normal
                          }`}
                        >
                          {URGENCY_LABELS[
                            ticket.urgency
                          ] ||
                            ticket.urgency}
                        </span>
                      </div>

                      <div className="truncate text-sm font-semibold">
                        {ticket.clientName}
                      </div>

                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold">
                          {ticket.machineName}
                        </div>

                        {ticket.machineSerialNumber && (
                          <div className="truncate text-xs text-[var(--do-muted)]">
                            {ticket.machineSerialNumber}
                          </div>
                        )}
                      </div>

                      <div className="truncate text-sm">
                        {ticket.technicianName ||
                          "Non affecté"}
                      </div>

                      <div>
                        <span
                          className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-bold ${
                            STATUS_CLASSES[
                              ticket.status
                            ] || ""
                          }`}
                        >
                          {STATUS_LABELS[
                            ticket.status
                          ] ||
                            ticket.status}
                        </span>
                      </div>

                      <div className="flex items-center justify-between gap-2 text-sm font-semibold">
                        <span>
                          {minutesLabel(
                            ticket.sla
                              ?.elapsedMinutes
                          )}
                        </span>

                        <CaretRight
                          size={16}
                          className="text-[var(--do-muted)]"
                        />
                      </div>
                    </Link>
                  )
                )}
              </div>
            </div>

            <div className="space-y-2 md:hidden">
              {visibleTickets.map(
                (ticket) => (
                  <Link
                    key={ticket.id}
                    to={ticketTarget(
                      ticket
                    )}
                    className="block rounded-xl border border-[var(--do-border)] bg-[var(--do-surface)] p-3 transition active:bg-[var(--do-surface-soft)]"
                    data-testid="sav-ticket-row-mobile"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span
                          className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${
                            URGENCY_CLASSES[
                              ticket.urgency
                            ] ||
                            URGENCY_CLASSES.normal
                          }`}
                        >
                          {URGENCY_LABELS[
                            ticket.urgency
                          ] ||
                            ticket.urgency}
                        </span>

                        <span
                          className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${
                            STATUS_CLASSES[
                              ticket.status
                            ] || ""
                          }`}
                        >
                          {STATUS_LABELS[
                            ticket.status
                          ] ||
                            ticket.status}
                        </span>
                      </div>

                      <span className="whitespace-nowrap text-xs font-semibold text-[var(--do-muted)]">
                        {minutesLabel(
                          ticket.sla
                            ?.elapsedMinutes
                        )}
                      </span>
                    </div>

                    <div className="mt-2 flex items-center justify-between gap-3">
                      <div className="font-heading text-sm font-black">
                        {ticket.ticketNumber}
                      </div>

                      <CaretRight
                        size={17}
                        className="text-[var(--do-muted)]"
                      />
                    </div>

                    <div className="mt-1 text-sm font-semibold">
                      {ticket.clientName}
                    </div>

                    <div className="mt-0.5 text-xs text-[var(--do-muted)]">
                      {ticket.machineName}
                      {ticket.machineSerialNumber
                        ? ` · ${ticket.machineSerialNumber}`
                        : ""}
                    </div>

                    <p className="mt-2 line-clamp-2 text-sm">
                      {ticket.description}
                    </p>

                    <div className="mt-2 flex items-center justify-between gap-3 border-t border-[var(--do-border)] pt-2 text-xs text-[var(--do-muted)]">
                      <span className="truncate">
                        {ticket.technicianName ||
                          "Non affecté"}
                      </span>

                      <span>
                        {formatDate(
                          ticket.createdAt
                        )}
                      </span>
                    </div>
                  </Link>
                )
              )}
            </div>
          </>
        )}
      </section>

      <SavTicketFormModal
        open={createOpen}
        machines={machines}
        saving={saving}
        onClose={() => {
          if (!saving) {
            setCreateOpen(false);
          }
        }}
        onSubmit={createTicket}
      />
    </main>
  );
}
